import {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {AMENITIES, BED_CATEGORIES, BRAND, DEPOSIT, CRASHPAD} from './site'
import {Container, PageHero} from './ui'
import {useLightbox} from './Lightbox'
import {useCrashpadAvailability, CrashpadBed} from './availability'
import {BedType} from '../../types/domain'
import {api, GalleryItem, PublicBedStatus, listingImageSrc} from '../../services/api'
import {useAuth} from '../../modules/auth'

const STATUS_META: Record<PublicBedStatus, {label: string; fg: string; bg: string}> = {
  AVAILABLE: {label: 'Available', fg: '#1f9d5b', bg: 'rgba(3,149,64,0.12)'},
  RESERVED: {label: 'Currently Reserved', fg: '#d9214e', bg: 'rgba(217,33,78,0.1)'},
  OCCUPIED: {label: 'Currently Occupied', fg: '#d9214e', bg: 'rgba(217,33,78,0.1)'},
  OUT_OF_SERVICE: {label: 'Temporarily Unavailable', fg: '#b8770a', bg: 'rgba(255,168,0,0.14)'},
}

const DEFAULT_GALLERY: GalleryItem[] = [
  {image: '/images/bunkroom.webp', label: 'The space'},
  {image: '/images/kitchen.webp', label: 'Kitchen'},
  {image: '/images/lounge.webp', label: 'Lounge'},
  {image: '/images/bathroom.webp', label: 'Shared bathroom'},
]

const CATEGORY_ICON: Record<BedType, string> = {
  [BedType.TWIN]: '🛏️',
  [BedType.FULL]: '🛏️',
  [BedType.QUEEN]: '👑',
}

/** A single bed listing — always visible; only status + button change. */
const BedCard = ({bed}: {bed: CrashpadBed}) => {
  const {currentUser} = useAuth()
  const {open} = useLightbox()
  // Deep-link to the bed's room page and pre-select this exact bed there.
  const reserveHref = bed.slug ? `/rooms/${bed.slug}?bed=${bed.code}` : '/auth/login'
  const meta = STATUS_META[bed.status]
  // Guests see the full mandatory §5 warning; signed-in renters a short form.
  const warning = currentUser ? DEPOSIT.short : DEPOSIT.warning
  return (
    <div
      className='ccp-lift'
      style={{
        background: '#fff',
        border: '1px solid #eef0f4',
        borderRadius: 18,
        padding: '22px 22px 20px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 6px 18px rgba(27,27,41,0.04)',
      }}
    >
      {/* This bed's own labeled photo — part of its image group. Click to zoom. */}
      {bed.photo && (
        <div
          className='ccp-zoom'
          onClick={() => open([{src: listingImageSrc(bed.photo as string), title: `Bed ${bed.code}`}], 0)}
          style={{position: 'relative', borderRadius: 12, overflow: 'hidden', aspectRatio: '4 / 3', marginBottom: 16, cursor: 'zoom-in'}}
        >
          <img src={listingImageSrc(bed.photo)} alt={`Bed ${bed.code}`} loading='lazy' style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}} />
          <div style={{position: 'absolute', left: 10, top: 10, background: 'rgba(0,0,0,0.62)', color: '#fff', fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', borderRadius: 999, padding: '4px 11px'}}>Bed {bed.code}</div>
        </div>
      )}
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10}}>
        <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
          <span style={{width: 46, height: 46, borderRadius: 12, background: 'linear-gradient(135deg, rgba(3,149,64,0.12), rgba(3,149,64,0.04))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22}}>
            {CATEGORY_ICON[bed.type]}
          </span>
          <div>
            <div style={{fontSize: 20, fontWeight: 800, color: BRAND.ink, lineHeight: 1.1}}>Bed {bed.code}</div>
            <div style={{fontSize: 13, color: BRAND.muted, fontWeight: 600}}>{labelFor(bed.type)}</div>
          </div>
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: meta.fg,
            background: meta.bg,
            borderRadius: 999,
            padding: '4px 11px',
            whiteSpace: 'nowrap',
          }}
        >
          {meta.label}
        </span>
      </div>

      <div style={{display: 'flex', alignItems: 'baseline', gap: 4, margin: '16px 0 14px'}}>
        <span style={{fontSize: 26, fontWeight: 800, color: BRAND.green}}>${bed.price}</span>
        <span style={{fontSize: 14, fontWeight: 600, color: BRAND.muted}}>/ month</span>
      </div>

      {bed.available ? (
        <Link
          to={reserveHref}
          className='ccp-cta'
          style={{display: 'block', textAlign: 'center', textDecoration: 'none', fontWeight: 700, fontSize: 15, color: '#fff', background: BRAND.green, borderRadius: 12, padding: '12px 18px'}}
        >
          Reserve This Bed
        </Link>
      ) : (
        <>
          <button
            disabled
            style={{width: '100%', fontWeight: 700, fontSize: 15, color: '#94a3b8', background: '#f1f3f7', border: 'none', borderRadius: 12, padding: '12px 18px', cursor: 'not-allowed'}}
          >
            {meta.label}
          </button>
          {/* Optional waitlist: take the renter into the reservation flow with
              this bed pre-selected; it's captured as a WAITLISTED booking. */}
          <Link
            to={bed.slug ? `/rooms/${bed.slug}?bed=${bed.code}&waitlist=1` : '/auth/login'}
            style={{display: 'block', textAlign: 'center', textDecoration: 'none', fontWeight: 700, fontSize: 14, color: BRAND.green, background: 'rgba(3,149,64,0.08)', border: '1px solid rgba(3,149,64,0.25)', borderRadius: 12, padding: '10px 18px', marginTop: 10}}
          >
            Join Waitlist
          </Link>
        </>
      )}

      {/* Mandatory 24-hour post-deposit approval warning on every bed card.
          Full verbatim warning for guests; short form once signed in. */}
      <p style={{fontSize: 12, lineHeight: 1.5, color: BRAND.muted, margin: '14px 0 0', borderTop: '1px solid #f1f3f7', paddingTop: 12}}>
        ⚠️ {warning}
      </p>
    </div>
  )
}

const labelFor = (type: BedType) => BED_CATEGORIES.find((c) => c.type === type)?.label ?? type

const RoomsPage = () => {
  const {open} = useLightbox()
  const [gallery, setGallery] = useState<GalleryItem[]>(DEFAULT_GALLERY)
  useEffect(() => {
    api.getSiteGallery().then((r) => {
      if (r.gallery?.length) setGallery(r.gallery)
    }).catch(() => {})
  }, [])
  const galleryItems = gallery.map((g) => ({src: listingImageSrc(g.image), title: g.label}))
  const {beds, totalBeds, availableBeds, loading} = useCrashpadAvailability()

  const bedsByType = (type: BedType) => beds.filter((b) => b.type === type)

  return (
    <div>
      <PageHero
        title='Accommodations'
        sub='Bed-size options within one shared crashpad — not separate rooms or apartments.'
      />

      {/* Crashpad-wide statement + live inventory (stated once, above the beds) */}
      <section style={{padding: '48px 0 8px'}}>
        <Container>
          <div style={{background: 'linear-gradient(160deg, #f1faf4, #ffffff)', border: '1px solid #e6f3ea', borderRadius: 18, padding: '26px 28px', display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center', justifyContent: 'space-between'}}>
            <div style={{maxWidth: 560}}>
              <h2 style={{fontSize: 22, fontWeight: 800, color: BRAND.ink, margin: '0 0 6px'}}>One shared crashpad</h2>
              <p style={{color: BRAND.muted, fontSize: 15, lineHeight: 1.6, margin: 0}}>{CRASHPAD.description}</p>
            </div>
            <div style={{display: 'flex', gap: 28}}>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: 34, fontWeight: 800, color: BRAND.ink, lineHeight: 1}}>{totalBeds}</div>
                <div style={{fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: BRAND.muted, marginTop: 4}}>Total beds</div>
              </div>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: 34, fontWeight: 800, color: BRAND.green, lineHeight: 1}}>{loading ? '—' : availableBeds}</div>
                <div style={{fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: BRAND.muted, marginTop: 4}}>Currently available</div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Bed listings grouped by size */}
      <section style={{padding: '32px 0 16px'}}>
        <Container>
          {BED_CATEGORIES.map((cat) => {
            const catBeds = bedsByType(cat.type)
            const avail = catBeds.filter((b) => b.available).length
            return (
              <div key={cat.type} style={{marginBottom: 40}}>
                <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 10, marginBottom: 16}}>
                  <h3 style={{fontSize: 22, fontWeight: 800, color: BRAND.ink, margin: 0}}>{cat.label} Beds</h3>
                  <span style={{fontSize: 14, color: BRAND.muted}}>
                    {cat.codes.length} total: {cat.codes.join(', ')} · ${cat.price} each / month · {loading ? '—' : avail} of {cat.codes.length} available
                  </span>
                </div>
                <div className='row g-3'>
                  {catBeds.map((bed) => (
                    <div className='col-12 col-sm-6 col-lg-4' key={bed.code}>
                      <BedCard bed={bed} />
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
          <p style={{color: BRAND.muted, fontSize: 14, marginTop: 4}}>
            Questions before you reserve? <Link to='/contact' style={{color: BRAND.green, fontWeight: 600, textDecoration: 'none'}}>Ask us</Link>.
          </p>
        </Container>
      </section>

      {/* Amenities */}
      <section style={{padding: '40px 0 56px', background: BRAND.soft}}>
        <Container>
          <h2 style={{fontSize: 26, fontWeight: 800, color: BRAND.ink, textAlign: 'center', margin: '0 0 32px'}}>Included with every bed</h2>
          <div className='row g-4'>
            {AMENITIES.map((a) => (
              <div className='col-6 col-md-4 col-lg-2' key={a.label}>
                <div className='ccp-lift' style={{background: '#fff', border: '1px solid #eef0f4', borderRadius: 16, padding: '24px 14px', textAlign: 'center', height: '100%', boxShadow: '0 4px 12px rgba(27,27,41,0.03)'}}>
                  <div style={{fontSize: 30, marginBottom: 10}}>{a.icon}</div>
                  <div style={{fontSize: 14, fontWeight: 600, color: BRAND.ink}}>{a.label}</div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Photo strip */}
      <section style={{padding: '40px 0 64px'}}>
        <Container>
          <div className='row g-3'>
            {gallery.map((g, i) => (
              <div className='col-6 col-lg-3' key={`${g.image}-${i}`}>
                <div className='ccp-lift ccp-zoom' onClick={() => open(galleryItems, i)} style={{position: 'relative', borderRadius: 16, overflow: 'hidden', aspectRatio: '4 / 3', boxShadow: '0 6px 18px rgba(27,27,41,0.08)', cursor: 'zoom-in'}}>
                  <img src={listingImageSrc(g.image)} alt={g.label} loading='lazy' style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}} />
                  <div style={{position: 'absolute', left: 0, right: 0, bottom: 0, padding: '18px 12px 9px', background: 'linear-gradient(transparent, rgba(0,0,0,0.6))', color: '#fff', fontSize: 12, fontWeight: 600}}>{g.label}</div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section style={{position: 'relative', overflow: 'hidden', background: `linear-gradient(135deg, ${BRAND.greenDark}, ${BRAND.green})`, color: '#fff'}}>
        <div className='ccp-blob' style={{background: '#0ad66a', width: 300, height: 300, top: -130, left: '15%'}} />
        <div className='ccp-blob' style={{background: '#016b2f', width: 300, height: 300, bottom: -150, right: '10%'}} />
        <Container style={{position: 'relative', padding: '56px 20px', textAlign: 'center'}}>
          <h2 style={{color: '#fff', fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: 800, margin: '0 0 12px'}}>Reserve your bed in San Francisco</h2>
          <p style={{fontSize: 16, opacity: 0.95, margin: '0 auto 26px', maxWidth: 560}}>{DEPOSIT.short}</p>
          <Link to='/auth/registration' className='ccp-cta-light' style={{textDecoration: 'none', fontWeight: 700, fontSize: 16, color: BRAND.green, background: '#fff', borderRadius: 12, padding: '15px 32px', display: 'inline-block'}}>
            Request a Reservation
          </Link>
        </Container>
      </section>
    </div>
  )
}

export {RoomsPage}
